import { Trash } from '@phosphor-icons/react';
import { cloneElement, ReactElement, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button, Confirmation } from 'core/components';
import { useAgGridClientTableContext } from 'core/states';
import { useElementTemplateActions } from 'elements/hooks';
import { IElementTemplate } from 'elements/types';
import { Color, Variant } from 'theme/enums';

interface IProps {
  elementTemplates?: IElementTemplate[];
  buttonElement?: ReactElement;
}

export const RemoveElementTemplatesButton = ({ elementTemplates, buttonElement }: IProps) => {
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);

  const { t } = useTranslation();

  const { updateRows, deselectAllRows, selectedRows } = useAgGridClientTableContext();

  const templatesToRemove = useMemo(
    () => elementTemplates ?? selectedRows ?? [],
    [elementTemplates, selectedRows]
  );

  const { removeElementTemplates } = useElementTemplateActions({
    selectionCount: templatesToRemove.length,
  });

  const onRemove = useCallback(async () => {
    setIsConfirmationOpen(false);

    const ids = templatesToRemove.map((template) => template.id);

    const { success } = await removeElementTemplates({ ids });

    if (!success) {
      return;
    }

    updateRows({ remove: templatesToRemove });

    if (!elementTemplates) {
      deselectAllRows();
    }
  }, [deselectAllRows, elementTemplates, removeElementTemplates, templatesToRemove, updateRows]);

  const button = useMemo(() => {
    if (buttonElement) {
      return cloneElement(buttonElement, {
        onClick: () => setIsConfirmationOpen(true),
      });
    }

    return (
      <Button
        text={t('buttonLabelRemove')}
        color={Color.DANGER}
        variant={Variant.SOFT}
        startDecorator={<Trash />}
        onClick={() => setIsConfirmationOpen(true)}
      />
    );
  }, [buttonElement, t]);

  return (
    <>
      {button}

      <Confirmation
        isOpen={isConfirmationOpen}
        title={t('titleRemoveElementTemplates', { count: templatesToRemove.length })}
        message={t('messageConfirmRemoveElementTemplates', { count: templatesToRemove.length })}
        confirmBtnLabel={t('buttonLabelRemove')}
        confirmBtnColor={Color.DANGER}
        onConfirm={onRemove}
        onCancel={() => setIsConfirmationOpen(false)}
      />
    </>
  );
};
